// src/Room.jsx - simplified approach
import { useLoader } from "@react-three/fiber";
import { TextureLoader } from "three/src/loaders/TextureLoader";
import { useEffect, useState, useRef, useContext } from "react";
import * as THREE from "three";
import {
  Gltf,
  useGLTF,
  SpotLight,
  MeshReflectorMaterial,
} from "@react-three/drei";
import { ControlContext } from "./ControlContext";
import { withCrossOrigin } from "./utils/ensureTextureCrossOrigin";
import SimpleWall from "./components/SimpleWall";
import SimpleCeiling from "./components/SimpleCeiling";

// At the top of the file, after imports
const REQUIRED_MODELS = [
  "/models/Baked_Sofa.glb",
  "/models/Baked_Rug.glb",
  "/models/Baked_Ceiling.glb",
  "/models/Baked_Lamp.glb",
  "/models/Baked_Table.glb",
  "/models/Baked_Baseboard.glb",
  "/models/Baked_Crown.glb",
];

// Preload all models
REQUIRED_MODELS.forEach((path) => useGLTF.preload(path));

const configureMeshMaterials = (scene, options = {}) => {
  if (!scene || !scene.scene) return;
  scene.scene.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = options.castShadow || false;
      child.receiveShadow = options.receiveShadow || false;

      if (child.material) {
        const applyMaterial = (originalMaterial) => {
          if (options.isColorizable) {
            const standardMaterial = new THREE.MeshStandardMaterial({
              transparent: originalMaterial.transparent,
              opacity: originalMaterial.opacity,
              side: THREE.DoubleSide,
              depthWrite: true,
              depthTest: true,
              roughness: 1,
              metalness: 0,
              envMapIntensity: 1,
            });

            if (originalMaterial.map) {
              standardMaterial.map = originalMaterial.map;
              standardMaterial.map.colorSpace = THREE.SRGBColorSpace;
              standardMaterial.map.flipY = false;
            }

            standardMaterial.color.setRGB(1, 1, 1);
            return standardMaterial;
          }
          return options.receiveShadow
            ? new THREE.MeshStandardMaterial({
                map: originalMaterial.map,
                transparent: originalMaterial.transparent,
                opacity: originalMaterial.opacity,
                side: THREE.DoubleSide,
                depthWrite: true,
                depthTest: true,
                roughness: 1,
                metalness: 0,
                envMapIntensity: 1,
              })
            : new THREE.MeshBasicMaterial({
                map: originalMaterial.map,
                transparent: originalMaterial.transparent,
                opacity: originalMaterial.opacity,
                side: THREE.DoubleSide,
                depthWrite: true,
                depthTest: true,
              });
        };

        if (Array.isArray(child.material)) {
          child.material = child.material.map(applyMaterial);
        } else {
          child.material = applyMaterial(child.material);
        }
      }
    }
  });
};

export default function Room() {
  const { cameraType } = useContext(ControlContext);
  const [target] = useState(() => new THREE.Object3D());
  const floorRef = useRef();

  // This will throw a promise if any model isn't loaded,
  // which Suspense will catch
  REQUIRED_MODELS.forEach((path) => {
    useGLTF(path);
  });

  const baked_sofa = useGLTF("/models/Baked_Sofa.glb");
  const baked_rug = useGLTF("/models/Baked_Rug.glb");
  const baked_lamp = useGLTF("/models/Baked_Lamp.glb");
  const baked_table = useGLTF("/models/Baked_Table.glb");
  const baked_baseboard = useGLTF("/models/Baked_Baseboard.glb");
  const baked_crown = useGLTF("/models/Baked_Crown.glb");

  useEffect(() => {
    if (floorRef.current) {
      floorRef.current.traverse((child) => {
        if (child.isMesh) {
          child.userData.isEnvironment = true;
        }
      });
    }
  }, []);

  useEffect(() => {
    // Configure all models
    const models = {
      sofa: baked_sofa,
      rug: baked_rug,
      lamp: baked_lamp,
      table: baked_table,
      baseboard: baked_baseboard,
      crown: baked_crown,
    };

    Object.entries(models).forEach(([_, model]) => {
      configureMeshMaterials(model, {
        castShadow: false,
        receiveShadow: true,
      });
      model.scene.traverse((child) => {
        child.userData.isEnvironment = true;
      });
    });
  }, [
    baked_sofa,
    baked_rug,
    baked_lamp,
    baked_table,
    baked_baseboard,
    baked_crown,
  ]);

  const [shouldShow, setShouldShow] = useState(false);
  useEffect(() => {
    if (cameraType === "Room" || cameraType === "Frame") {
      setShouldShow(true);
    } else {
      const timer = setTimeout(() => {
        setShouldShow(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cameraType]);

  // Update this useEffect to use the shouldShow state
  useEffect(() => {
    // Function to update visibility of environment objects
    const updateEnvironmentVisibility = () => {
      // Update floor
      if (floorRef.current) {
        floorRef.current.traverse((child) => {
          if (child.userData.isEnvironment) {
            child.visible = shouldShow;
          }
        });
      }

      // Update all loaded models
      const models = {
        sofa: baked_sofa,
        rug: baked_rug,
        lamp: baked_lamp,
        table: baked_table,
        baseboard: baked_baseboard,
        crown: baked_crown,
      };

      Object.values(models).forEach((model) => {
        if (model && model.scene) {
          model.scene.traverse((child) => {
            if (child.userData.isEnvironment) {
              child.visible = shouldShow;
            }
          });
        }
      });
    };

    updateEnvironmentVisibility();
  }, [
    shouldShow,
    baked_sofa,
    baked_rug,
    baked_lamp,
    baked_table,
    baked_baseboard,
    baked_crown,
  ]);

  const [spotlightMap, floorColorMap, floorRoughnessMap, floorNormalMap] =
    useLoader(
      TextureLoader,
      [
        "./textures/gobos/GSG_GC006_A028_Window08.jpg",
        "./models/FLOOR.png",
        "./models/GSG_MC001_A326_DWoodCaramelOakAgedPlank_4k_roughness.webp",
        "./models/GSG_MC001_A326_DWoodCaramelOakAgedPlank_4k_normal.webp",
      ],
      withCrossOrigin
    );

  return (
    <>
      <SimpleWall />
      <SimpleCeiling />
      <mesh
        ref={floorRef}
        receiveShadow
        position={[0, -160, 250]}
        rotation-x={Math.PI / -2}
        rotation-z={Math.PI}
      >
        <planeGeometry args={[500, 500]} />
        <MeshReflectorMaterial
          map={floorColorMap}
          roughnessMap={floorRoughnessMap}
          normalMap={floorNormalMap}
          blur={[5, 5]}
          mixBlur={2}
          mixStrength={0.5}
          resolution={512}
          mirror={0}
        />
      </mesh>

      <primitive
        object={baked_sofa.scene}
        position={[0, 0, 0]}
        scale={[100, 100, 100]}
        rotation={[0, -Math.PI, 0]}
        frustumCulled={false}
      />

      <primitive
        object={baked_rug.scene}
        position={[0, 0, 0]}
        scale={[100, 100, 100]}
        rotation={[0, -Math.PI, 0]}
        frustumCulled={false}
      />

      <primitive
        object={baked_lamp.scene}
        position={[0, 0, 0]}
        scale={[100, 100, 100]}
        rotation={[0, -Math.PI, 0]}
        frustumCulled={false}
      />

      <primitive
        object={baked_table.scene}
        position={[0, 0, 0]}
        scale={[100, 100, 100]}
        rotation={[0, -Math.PI, 0]}
        frustumCulled={false}
      />

      <primitive
        object={baked_baseboard.scene}
        position={[0, 0, 0]}
        scale={[100, 100, 100]}
        rotation={[0, -Math.PI, 0]}
        frustumCulled={false}
      />

      <primitive
        object={baked_crown.scene}
        position={[0, 0, 0]}
        scale={[100, 100, 100]}
        rotation={[0, -Math.PI, 0]}
        frustumCulled={false}
      />

      <primitive object={target} position={[-100, -100, 0]} />

      <SpotLight
        castShadow
        target={target}
        penumbra={1}
        radiusTop={1}
        radiusBottom={2000}
        distance={1000}
        angle={0.1}
        attenuation={100}
        anglePower={20}
        intensity={1000000}
        opacity={0}
        position={[-600, 0, 600]}
        map={spotlightMap}
      />
    </>
  );
}
